/**
 * Terravision — design tokens (refonte institutionnelle)
 * Source de vérité de la nouvelle charte. Aligné avec tailwind.config.ts.
 * J5 Vague 1 — design system foundation.
 */
export const colors = {
  ink: '#11193a',
  navy: { DEFAULT: '#1b2559', 600: '#2c3a78', 900: '#11193a', rail: '#161d3c' },
  marianne: '#e1000f',   // accent rouge — usage parcimonieux (CTA critique, alertes, dépassement ZAN)
  blue: '#2563eb',       // lien / data secondaire (cohérent avec le logo B.I.A)
  surface: { page: '#eef1f6', card: '#ffffff', subtle: '#f6f8fb', panel: '#f1f4f9' },
  line: '#e4e8f0',
  text: { strong: '#11193a', body: '#41496a', muted: '#6b7392', faint: '#8b93a9' },
  state: {
    success: '#1f8a5b', successBg: '#eaf6ef',
    warn: '#ef7d18', warnBg: '#fff4e5',
    danger: '#e1000f', dangerBg: '#fdecec',
  },
} as const;

/** Étiquettes DPE (diagnostic de performance énergétique). */
export const dpe = { A: '#2e9e44', B: '#74c34a', C: '#c4cf1e', D: '#f3d20f', E: '#f5a623', F: '#ef7d18', G: '#e30613' } as const;

/** Rampes séquentielles data-viz. Stops normalisés [0..1]. */
export const ramps = {
  densite: [[0, '#e7ecf7'], [0.5, '#7b8cc6'], [1, '#1b2559']],
  thermal: [[0, '#1f9d6b'], [0.34, '#cbe04a'], [0.55, '#ffd24a'], [0.72, '#ff9f3c'], [0.88, '#ff5a36'], [1, '#cc1f1f']],
  solar: [[0, '#fff3c4'], [0.45, '#ffd24a'], [0.75, '#ff8c1a'], [1, '#e1000f']],
} as const;

/** Occupation des sols (catégoriel). */
export const sols = { artificialise: '#8a93a8', agricole: '#cf9d4a', naturel: '#4f9d6b' } as const;

export const fonts = {
  display: "'Libre Franklin', system-ui, sans-serif", // titres
  body: "'Public Sans', system-ui, sans-serif",       // UI / corps / chiffres
  mono: "'IBM Plex Mono', ui-monospace, monospace",    // codes INSEE, coordonnées, axes
} as const;

export const radii = { card: 14, panel: 16, pill: 10, input: 11, full: 9999 } as const;

export const shadows = {
  card: '0 1px 2px rgba(17,25,58,.04)',
  float: '0 8px 24px -16px rgba(17,25,58,.18)',
  overlay: '0 6px 18px -8px rgba(17,25,58,.30)',
} as const;

/** Échelle d'espacement (px), base 4. */
export const space = [0, 4, 8, 12, 16, 20, 24, 32, 40] as const;

// ── Plan Canicule (J6) ───────────────────────────────────────────────

/** Vigilance Météo-France (4 niveaux). Jamais codée par la SEULE couleur : toujours icône + libellé. */
export const vigilance = {
  vert:   { color: '#1f8a5b', bg: '#eaf6ef', label: 'Vert' },
  jaune:  { color: '#f3d20f', bg: '#fef7e0', label: 'Jaune' },
  orange: { color: '#ef7d18', bg: '#fff1ea', label: 'Orange' },
  rouge:  { color: '#e1000f', bg: '#fdecec', label: 'Rouge' },
} as const;

/** Bandeau d'épisode actif (Plan Canicule). */
export const vigilanceBanner = 'linear-gradient(100deg,#c10f2e,#ef5a2a)';

/** Rampes data-viz du Plan Canicule (croisement Exposition × Vulnérabilité × Accès). */
export const heatwaveRamps = {
  priority:      [[0, '#ffe9c7'], [0.4, '#ffba5a'], [0.7, '#ff7a3c'], [0.88, '#e1000f'], [1, '#8a0b1a']],
  exposition:    [[0, '#1f9d6b'], [0.34, '#cbe04a'], [0.55, '#ffd24a'], [0.72, '#ff9f3c'], [0.88, '#ff5a36'], [1, '#cc1f1f']],
  vulnerabilite: [[0, '#efe7f7'], [0.5, '#b79bd8'], [1, '#6b3fa0']],
  acces:         [[0, '#1f9d6b'], [0.45, '#ffd24a'], [1, '#e1000f']],
} as const;

/** Symboles carte (différenciés par FORME, pas seulement couleur) : refuge = cercle, équipement sensible = carré. */
export const heatwaveSymbols = {
  refugeClim: '#2563eb',        // cercle bleu
  refugePark: '#1f8a5b',        // cercle vert (parc / point d'eau)
  sensibleCritical: '#e1000f',  // carré rouge
  sensibleWatch: '#e08600',     // carré ambre
} as const;

// ── Rénovation énergétique (9ᵉ écran) ────────────────────────────────

/** Rampes data-viz du module Rénovation énergétique (Passoires × Précarité × Gisement). */
export const renovationRamps = {
  priority:  [[0, '#eaf2fb'], [0.4, '#9bbce6'], [0.7, '#3b82f6'], [0.88, '#1d4ed8'], [1, '#11235e']],
  passoires: [[0, '#fff3df'], [0.5, '#ef7d18'], [1, '#e30613']],
  precarite: [[0, '#efe7f7'], [0.5, '#b79bd8'], [1, '#6b3fa0']],
  gisement:  [[0, '#e6f4ee'], [0.5, '#5cbd92'], [1, '#1f8a5b']],
} as const;

/** Bandeau d'échéance Loi Climat & Résilience (institutionnel, distinct du bandeau vigilance rouge). */
export const scheduleBanner = 'linear-gradient(100deg,#161d3c,#2c3a78)';

/** Statut d'occupation du bâti prioritaire (marqueurs carte Rénovation). */
export const tenure = { copro: '#1b2559', social: '#2563eb', communal: '#e08600' } as const;
